import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { TransactionStats } from "@/components/transactions/TransactionStats";
import { TransactionFilters } from "@/components/transactions/TransactionFilters";
import { TransactionTableSkeleton } from "@/components/transactions/TransactionTableSkeleton";
import { TransactionDetailsModal } from "@/components/transactions/TransactionDetailsModal";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  Check,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  CalendarDays,
  ChevronsLeft,
  ChevronLeft,
  ChevronsRight,
  ExternalLink,
} from "lucide-react";
import { ProgramApi } from "@/api/program.api";
import { TransactionApi } from "@/api/transaction.api";
import {
  downloadTransactionsCSV,
  downloadTransactionsPDF,
  TransactionExportData,
} from "@/utils/pdfGenerator";
import { downloadResponseFile } from "@/utils/common.utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import { Input } from "@/components/ui/input";
import {
  addDays,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
} from "date-fns";
import { useLocation } from "react-router-dom";
import { DateRange } from "react-day-picker";
import { useDebounce } from "@/hooks/use-debounce";
import type { UserInfo } from "@/hooks/use-user-info";
import {
  fetchMerchantsForUserFilter,
  getDefaultMerchantFilterCode,
  getMerchantFilterParams,
} from "@/utils/merchant-filter.utils";
import { isLoyyalAdmin } from "@/utils/role.utils";
import { getFormattedDateTime } from "@/utils";

interface TransactionsProps {
  showHeader?: boolean;
  /** When true, parent owns the program dropdown (e.g. Reports tabs row) */
  hideProgramDropdown?: boolean;
  /** Controlled program selection from parent */
  programCode?: string;
  onProgramChange?: (programCode: string) => void;
}

const Transactions: React.FC<TransactionsProps> = ({
  showHeader = true,
  hideProgramDropdown = false,
  programCode: controlledProgramCode,
  onProgramChange,
}) => {
  const location = useLocation();
  const userInfo: UserInfo | null = (() => {
    try {
      const stored = sessionStorage.getItem("userInfo");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const defaultMerchantCode = getDefaultMerchantFilterCode(userInfo);
  const loyyalAdmin = isLoyyalAdmin(userInfo?.roleCode);

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<any>(() =>
    defaultMerchantCode ? { merchantCode: defaultMerchantCode } : {},
  );
  const [dateRange, setDateRange] = useState(loyyalAdmin ? "none" : "30days");
  const [sortBy, setSortBy] = useState({ field: "", direction: "" });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState({
    totalNumberOfTransactions: 0,
    totalAmountOfTransactions: 0,
    totalPointsPending: 0,
    totalPointsEarned: 0,
    avgPointsPerTransaction: 0,
    currencyVO: null as {
      currencySymbol?: string;
      iso3CurrencyCode?: string;
    } | null,
  });
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [searchType, setSearchType] = useState("memberId");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined,
  );

  // Add debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Calculate total pages
  const totalPages = Math.ceil(totalTransactions / rowsPerPage);

  const fetchTransactions = useCallback(async () => {
    if (!filters.programCode) {
      setTransactions([]);
      setTotalTransactions(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const payload: any = {
        startDateInMillis: filters.startDateInMillis || "",
        endDateInMillis: filters.endDateInMillis || "",
        sortBy: sortBy.field || "transactionDate",
        sortOrder: (sortBy.direction as "asc" | "desc") || "desc",
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        status: filters.status || "", // accrual status filter (unchanged)
        transactionStatus:
          filters.orderStatus !== undefined && filters.orderStatus !== ""
            ? Number(filters.orderStatus)
            : "", // send numeric 0/1/2 when selected
        merchantTransactionId:
          searchType === "merchantTransactionId"
            ? debouncedSearchTerm.trim()
            : "",
        programMemberId:
          searchType === "memberId" ? debouncedSearchTerm.trim() : "",
        transactionAmount: filters.amount || "",
        id:
          searchType === "loyyalTxnId"
            ? debouncedSearchTerm.trim()
            : filters.id || "",
      };
      if (filters.programCode) {
        payload.programCode = filters.programCode;
      }
      Object.assign(payload, getMerchantFilterParams(userInfo, filters));
      const txRes =
        await TransactionApi.getTransactionsDashboardFromDB(payload);
      const transactionsData = txRes.data?.merchantTransactionsDataVO || {};
      const mappedTransactions = Array.isArray(
        transactionsData.merchantTransactionVOs,
      )
        ? transactionsData.merchantTransactionVOs
        : [];
      setTransactions(mappedTransactions);
      setTotalTransactions(
        Number(transactionsData.totalNumberOfTransactions) ||
          mappedTransactions.length,
      );

      const rawStats = txRes.data?.transactionStatsVOS;
      const statsFromApi = Array.isArray(rawStats)
        ? rawStats[0]
        : rawStats && typeof rawStats === "object"
          ? rawStats
          : null;
      setStats({
        totalNumberOfTransactions:
          statsFromApi?.totalSuccessfulTransactions ?? 0,
        totalAmountOfTransactions:
          statsFromApi?.totalSuccessfulTransactionAmount ?? 0,
        totalPointsPending: statsFromApi?.totalPointsPending ?? 0,
        totalPointsEarned: statsFromApi?.totalPointsEarned ?? 0,
        avgPointsPerTransaction: statsFromApi?.avgPointsPerTransaction ?? 0,
        currencyVO: statsFromApi?.currencyVO || null,
      });
    } catch (e) {
      setTransactions([]);
      setTotalTransactions(0);
      setStats({
        totalNumberOfTransactions: 0,
        totalAmountOfTransactions: 0,
        totalPointsPending: 0,
        totalPointsEarned: 0,
        avgPointsPerTransaction: 0,
        currencyVO: null,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    filters,
    sortBy,
    currentPage,
    rowsPerPage,
    searchType,
    debouncedSearchTerm,
    userInfo,
  ]);

  useEffect(() => {
    // Fetch merchants and programs on mount (stats come from dashboard API)
    const fetchData = async () => {
      try {
        const [merchantList, programRes] = await Promise.all([
          fetchMerchantsForUserFilter(userInfo),
          ProgramApi.getProgramsFromDB({
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "asc",
            sortBy: "programName",
            programCode: null,
            programName: null,
            programType: "EARN",
          }),
        ]);
        setMerchants(merchantList);
        const programsData = Array.isArray(programRes.data?.programVOs)
          ? programRes.data.programVOs
          : [];
        setPrograms(programsData);
      } catch (e) {
        setMerchants([]);
        setPrograms([]);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Fetch transactions for current page/size
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage, debouncedSearchTerm, filters, sortBy]);

  useEffect(() => {
    // On mount, check for programCode in query string
    const params = new URLSearchParams(location.search);
    const programCode = params.get("programCode");
    if (programCode) {
      setFilters((prev) => ({ ...prev, programCode }));
    }
  }, [location.search]);

  // Sync controlled program from parent (Reports page)
  useEffect(() => {
    if (controlledProgramCode === undefined) return;
    setFilters((prev) =>
      prev.programCode === controlledProgramCode
        ? prev
        : { ...prev, programCode: controlledProgramCode },
    );
  }, [controlledProgramCode]);

  // Auto-select the first program when programs load (unless URL or parent already set one)
  useEffect(() => {
    if (controlledProgramCode !== undefined) return;

    const params = new URLSearchParams(location.search);
    const urlProgramCode = params.get("programCode");

    if (programs.length > 0 && !filters.programCode && !urlProgramCode) {
      const firstProgram = programs[0];
      if (firstProgram?.programCode) {
        setFilters((prev) => ({
          ...prev,
          programCode: firstProgram.programCode,
        }));
      }
    }
    // eslint-disable-next-line
  }, [programs, controlledProgramCode]);

  const handleProgramChange = (programCode: string) => {
    if (onProgramChange) {
      onProgramChange(programCode);
    }
    setFilters((prev) => ({ ...prev, programCode }));
    setCurrentPage(0);
  };

  // Helper to get start and end in millis for a given range
  const getDateRangeMillis = (range) => {
    const now = new Date();
    let start, end;
    switch (range) {
      case "today":
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case "yesterday":
        start = startOfDay(addDays(now, -1));
        end = endOfDay(addDays(now, -1));
        break;
      case "7days":
        start = startOfDay(addDays(now, -6));
        end = endOfDay(now);
        break;
      case "30days":
        start = startOfDay(addDays(now, -29));
        end = endOfDay(now);
        break;
      case "thisMonth":
        start = startOfMonth(now);
        end = endOfDay(now);
        break;
      case "lastMonth":
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      default:
        start = null;
        end = null;
    }
    return {
      startDateInMillis: start ? start.getTime() : "",
      endDateInMillis: end ? end.getTime() : "",
    };
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(0);
  };

  // Only update searchType; do not trigger a search or reload if searchTerm is empty
  const handleSearchTypeChange = (type: string) => {
    // Only update searchType; do not trigger a search or reload if searchTerm is empty
    setSearchType(type);
    if (searchTerm.trim() !== "") {
      setCurrentPage(0);
    }
    // No-op if searchTerm is empty
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const handleExport = async (formatType: "csv" | "csv-all-programs") => {
    if (formatType === "csv" || formatType === "csv-all-programs") {
      try {
        const exportAllPrograms = formatType === "csv-all-programs";
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        const payload = {
          ...filters,
          startDateInMillis: filters.startDateInMillis || "",
          endDateInMillis: filters.endDateInMillis || "",
          sortBy: sortBy.field || "transactionDate",
          sortOrder: (sortBy.direction as "asc" | "desc") || "desc",
          pageNumber: 0,
          pageSize: 10000,
          status: filters.status || "",
          transactionStatus:
            filters.orderStatus !== undefined && filters.orderStatus !== ""
              ? Number(filters.orderStatus)
              : "", // send numeric 0/1/2 when selected
          merchantTransactionId:
            searchType === "merchantTransactionId" ? searchTerm.trim() : "",
          programMemberId: searchType === "memberId" ? searchTerm.trim() : "",
          transactionAmount: filters.amount || "",
          id:
            searchType === "loyyalTxnId" ? searchTerm.trim() : filters.id || "",
          programCode: exportAllPrograms ? "" : filters.programCode || "",
          ...getMerchantFilterParams(userInfo, filters),
          programType: "EARN",
        };
        const response = await fetch(
          `${API_BASE}/transaction/getTransactionsFromDbInCSVFormat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          },
        );
        if (!response.ok) {
          throw new Error("Failed to export CSV");
        }
        const dateSuffix = new Date().toISOString().split("T")[0];
        const filename = await downloadResponseFile(
          response,
          exportAllPrograms
            ? `transactions_all_programs_${dateSuffix}.csv`
            : `transactions_${dateSuffix}.csv`,
        );
        toast.success(`Transactions exported as ${filename} successfully!`);
      } catch (error) {
        console.error("Error exporting transactions:", error);
        toast.error("Failed to export transactions as CSV");
      }
      return;
    }
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    let newFilters;
    if (range === "none") {
      newFilters = {
        ...filters,
        startDateInMillis: "",
        endDateInMillis: "",
      };
    } else {
      const { startDateInMillis, endDateInMillis } = getDateRangeMillis(range);
      newFilters = {
        ...filters,
        startDateInMillis,
        endDateInMillis,
      };
    }
    handleFilterChange(newFilters);
  };

  const handleReset = () => {
    setSearchTerm("");
    // Keep programCode selection (top dropdown / API payload key) — only reset other filters
    const preservedProgramCode =
      controlledProgramCode || filters.programCode || "";
    setFilters({
      ...(defaultMerchantCode ? { merchantCode: defaultMerchantCode } : {}),
      ...(preservedProgramCode ? { programCode: preservedProgramCode } : {}),
    });
    setDateRange("none");
    setSortBy({ field: "", direction: "" });
    setCurrentPage(0);
    setCustomDateRange(undefined); // Reset the custom date range
    toast.success("All filters have been reset");
  };

  const handleSort = (field: string) => {
    // Map UI field names to API field names
    const apiFieldMap: Record<string, string> = {
      date: "transactionDate",
      amount: "transactionAmount",
      cashback: "transactionCashbackAmount",
    };

    const apiField = apiFieldMap[field] || field;

    if (sortBy.field === apiField) {
      // Toggle between asc and desc
      setSortBy({
        field: apiField,
        direction: sortBy.direction === "asc" ? "desc" : "asc",
      });
    } else {
      // First click: set to asc with arrow up
      setSortBy({ field: apiField, direction: "asc" });
    }
  };

  const handleViewDetails = (transaction: any) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalOpen(true);
  };

  const handleMarkRefunded = async (transactionId: string) => {
    try {
      await TransactionApi.updateTransactionStatus(transactionId, 2);
      toast.success(`Transaction ${transactionId} marked as refunded`);
      fetchTransactions();
    } catch (error) {
      toast.error(`Failed to mark transaction ${transactionId} as refunded`);
    }
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: string;
    children: React.ReactNode;
  }) => {
    // Map UI field names to API field names for comparison
    const apiFieldMap: Record<string, string> = {
      date: "transactionDate",
      amount: "transactionAmount",
      cashback: "transactionCashbackAmount",
    };

    const apiField = apiFieldMap[field] || field;
    const isSorted = sortBy.field === apiField;
    const direction = sortBy.direction;

    return (
      <div
        className="flex items-center cursor-pointer"
        onClick={() => handleSort(field)}
      >
        {children}
        <span className="ml-1">
          {isSorted && direction === "asc" && <ArrowUp className="h-4 w-4" />}
          {isSorted && direction === "desc" && (
            <ArrowDown className="h-4 w-4" />
          )}
          {!isSorted && <ChevronDown className="h-4 w-4 opacity-30" />}
        </span>
      </div>
    );
  };

  // Wrap the original handleFilterChange to reset date filter when any other filter is applied
  const handleOtherFilterChange = (newFilters: any) => {
    // Check if this is a date filter application (contains startDateInMillis or endDateInMillis)
    const isDateFilter =
      newFilters.startDateInMillis !== undefined ||
      newFilters.endDateInMillis !== undefined;

    if (isDateFilter) {
      handleFilterChange({
        ...filters,
        ...newFilters,
        programCode: filters.programCode,
      });
    } else {
      // This is another type of filter, clear date filters but keep program
      setDateRange("none");
      handleFilterChange({
        ...filters,
        ...newFilters,
        startDateInMillis: "",
        endDateInMillis: "",
        programCode: filters.programCode,
      });
    }
  };

  // Handler for custom date range picker - apply immediately when both dates are selected
  const handleCustomDateRangeChange = (range: DateRange | undefined) => {
    setCustomDateRange(range);

    // Apply the filter immediately when both dates are selected
    if (range && range.from && range.to) {
      const newFilters = {
        ...filters,
        startDateInMillis: startOfDay(range.from).getTime(),
        endDateInMillis: endOfDay(range.to).getTime(),
      };
      handleFilterChange(newFilters);
    }
  };

  // Helper function for sentence case
  function sentenceCase(str: string) {
    return (
      str.charAt(0).toUpperCase() +
      str.slice(1).toLowerCase().replace(/_/g, " ")
    );
  }

  return (
    <div className="space-y-6">
      {(showHeader || !hideProgramDropdown) && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {showHeader ? (
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Transactions</h1>
              <p className="text-gray-500 mt-1">
                View and filter all loyalty program transactions
              </p>
            </div>
          ) : (
            <div />
          )}
          {!hideProgramDropdown && (
            <div className="w-full sm:w-64 sm:ml-auto">
              <Select
                value={filters.programCode || ""}
                onValueChange={handleProgramChange}
                disabled={programs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((program: any) => (
                    <SelectItem
                      key={program.programCode}
                      value={program.programCode}
                    >
                      {program.programName || program.programCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Transaction Stats */}
      <TransactionStats
        isLoading={isLoading}
        totalTransactionsTitle="Total Successful Transactions"
        totalTransactionsDescription="Count of completed successful transactions"
        totalValueTitle="Total Successful Transaction Value"
        totalValueDescription="Monetary value of successful transactions"
        stats={{
          totalTransactions: stats.totalNumberOfTransactions ?? 0,
          totalValue: (
            <>
              {stats.totalAmountOfTransactions?.toLocaleString("en-US", {
                maximumFractionDigits: 2,
              }) ?? "0.00"}{" "}
              <span className="text-sm font-normal">
                {stats.currencyVO?.iso3CurrencyCode || ""}
              </span>
            </>
          ),
          pointsEarned: stats.totalPointsEarned ?? 0,
          pointsBurned: stats.totalPointsPending ?? 0,
          avgPointsPerTransaction: stats.avgPointsPerTransaction,
        }}
      />

      {/* Transaction Filters */}
      <TransactionFilters
        onFilterChange={handleOtherFilterChange}
        onExport={handleExport}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
        merchants={merchants}
        searchType={searchType}
        setSearchType={handleSearchTypeChange}
        searchTerm={searchTerm}
        onSearch={handleSearch}
        defaultMerchantCode={defaultMerchantCode}
        lockMerchantFilter={!!defaultMerchantCode}
        customDateRange={customDateRange}
        onCustomDateRangeChange={handleCustomDateRangeChange}
      />

      {/* Transactions Table */}
      {isLoading ? (
        <TransactionTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <TooltipProvider>
          <div className="w-full overflow-x-auto">
            <DataTable
              title="All Transactions"
              data={transactions.map((tx: any) => ({
                id: tx.merchantTransactionId || tx.id,
                transactionId: tx.id,
                merchant: tx.merchantVO?.merchantName || "",
                program: tx.programVO?.programName || "",
                amount: tx.transactionAmount,
                currency: tx.transactionCurrency,
                transactionStatus:
                  tx.transactionStatus === 1
                    ? "completed"
                    : tx.transactionStatus === 0
                      ? "pending"
                      : "refunded",
                accrualStatus:
                  tx.accrualStatus === "ACCRUAL_FAILED"
                    ? "accrual failed"
                    : tx.accrualStatus === "ACCRUAL_SUCCESSFUL"
                      ? "accrual successful"
                      : "not accrued",
                rawAccrualStatus: tx.accrualStatus,
                rawTransactionStatus: tx.transactionStatus,
                date: tx.transactionDate
                  ? tx.transactionDate.split("T")[0]
                  : "",
                time: tx.transactionDate
                  ? tx.transactionDate.split("T")[1]?.split(".")[0]
                  : "",
                transactionDate: tx.transactionDate,
                cashback: tx.transactionCashbackAmount,
                memberId: tx.memberVO?.programMemberId || "",
                points: tx.points ?? "",
                _original: tx, // Attach the original object here
              }))}
              columns={[
                {
                  header: <SortableHeader field="date">Date</SortableHeader>,
                  id: "datetime",
                  width: "180px",
                  cell: (row: any) => {
                    const formatted = row.transactionDate
                      ? getFormattedDateTime(row.transactionDate) ||
                        row.date ||
                        ""
                      : row.date || "";
                    return <span>{formatted}</span>;
                  },
                },
                {
                  header: "Loyyal Txn ID",
                  accessorKey: "transactionId",
                },
                {
                  header: "Merchant Txn Id",
                  accessorKey: "id",
                  cell: (row: any) => {
                    const merchantTxId = String(row.id);
                    const isTruncated = merchantTxId.length > 16;
                    const displayText = isTruncated
                      ? `${merchantTxId.slice(0, 16)}...`
                      : merchantTxId;

                    if (isTruncated) {
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer">
                              {displayText}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{merchantTxId}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return <span>{displayText}</span>;
                  },
                },
                {
                  header: "Program",
                  accessorKey: "program",
                  cell: (row: any) => {
                    if (!row.program) return "";
                    const words = row.program.split(" ");
                    const isTruncated = words.length > 2;
                    const displayText = isTruncated
                      ? `${words.slice(0, 2).join(" ")}...`
                      : row.program;

                    if (isTruncated) {
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>{displayText}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{row.program}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return <span>{displayText}</span>;
                  },
                },
                {
                  header: "Merchant",
                  accessorKey: "merchant",
                },
                {
                  header: "Member ID",
                  accessorKey: "memberId",
                },
                {
                  header: (
                    <SortableHeader field="amount">Amount</SortableHeader>
                  ),
                  accessorKey: "amount",
                  cell: (row: any) => (
                    <span>
                      {row.currency} {row.amount}
                    </span>
                  ),
                },
                {
                  header: (
                    <SortableHeader field="cashback">Cashback</SortableHeader>
                  ),
                  accessorKey: "cashback",
                  cell: (row: any) => (
                    <span>
                      {row.currency} {row.cashback}
                    </span>
                  ),
                },
                {
                  header: "Points",
                  accessorKey: "points",
                },
                {
                  header: "Transaction Status",
                  accessorKey: "accrualStatus",
                  cell: (row: any) => {
                    let label = "";
                    let badgeClass = "";
                    if (row.rawTransactionStatus === 0) {
                      label = "Not Finalized";
                      badgeClass = "bg-gray-100 text-gray-800";
                    } else if (row.rawTransactionStatus === 1) {
                      label = "Finalized";
                      badgeClass = "bg-blue-100 text-blue-800";
                    } else if (row.rawTransactionStatus === 2) {
                      label = "Refunded";
                      badgeClass = "bg-purple-100 text-purple-800";
                    }
                    return label ? (
                      <Badge variant="outline" className={badgeClass}>
                        {label}
                      </Badge>
                    ) : null;
                  },
                },
                {
                  header: "Accrual Status",
                  accessorKey: "transactionStatus",
                  cell: (row: any) => {
                    let label = "";
                    let badgeClass = "";
                    if (row.rawAccrualStatus === "ACCRUAL_SUCCESSFUL") {
                      label = "Earned";
                      badgeClass = "bg-green-100 text-green-800";
                    } else if (row.rawAccrualStatus === "ACCRUAL_FAILED") {
                      label = "Failed";
                      badgeClass = "bg-red-100 text-red-800";
                    } else if (row.rawAccrualStatus === "NOT_ACCRUED") {
                      label = "Not Accrued";
                      badgeClass = "bg-gray-100 text-gray-800";
                    } else if (row.rawAccrualStatus === "ACCURAL_IN_PROGRESS") {
                      label = "Pending";
                      badgeClass = "bg-yellow-100 text-yellow-800";
                    }
                    return label ? (
                      <Badge variant="outline" className={badgeClass}>
                        {label}
                      </Badge>
                    ) : null;
                  },
                },
                {
                  header: "Actions",
                  id: "actions",
                  cell: (row: any) => (
                    <div className="flex justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem
                            onClick={() => handleViewDetails(row._original)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            <span>View Details</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleMarkRefunded(row.transactionId)
                            }
                            disabled={
                              row.rawTransactionStatus === 2 ||
                              row.rawTransactionStatus === 1 ||
                              row.rawAccrualStatus === "ACCRUAL_SUCCESSFUL"
                            }
                          >
                            <Check className="mr-2 h-4 w-4" />
                            <span>Mark Refunded</span>
                          </DropdownMenuItem>
                          {row._original?.explorerUrl ? (
                            <DropdownMenuItem
                              onClick={() =>
                                window.open(
                                  row._original.explorerUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              <span>View On Blockchain</span>
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </TooltipProvider>
      )}

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
        item={selectedTransaction}
      />

      {/* Pagination controls */}
      {!isLoading && totalPages > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === 0
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                      aria-label="First page"
                      type="button"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                  {currentPage > 0 ? (
                    <PaginationPrevious
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(0, prev - 1))
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  ) : (
                    <PaginationPrevious
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber =
                      currentPage <= 2 ? i : currentPage + i - 2;
                    if (pageNumber >= totalPages) return null;
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNumber)}
                          isActive={currentPage === pageNumber}
                        >
                          {pageNumber + 1}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  {currentPage < totalPages - 1 ? (
                    <PaginationNext
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(totalPages - 1, prev + 1),
                        )
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  ) : (
                    <PaginationNext
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  )}
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === totalPages - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                      aria-label="Last page"
                      type="button"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
